import React, { useContext, useEffect, useReducer, useState } from "react";
import Screen from "utils/helpers/ScreenSize";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { MobXProviderContext, observer } from "mobx-react";
import SubmitButtons from "components/SubmitButtons";
import { useMutation } from "@tanstack/react-query";
import { ErrorWithSupport } from "components";
import dateTime from "utils/helpers/dateTime";
import { sanitizeAllowedChars, sanitizeAndTrimInput } from "utils/helpers/string";

function ManageOrderDetailsModal({ visible, setModal, order, getOrder }) {
  const screenType = Screen.ScreenType();
  const store = useContext(MobXProviderContext);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorSupport, setErrorSupport] = useState(false);
  const [errorTitle, setErrorTitle] = useState("");
  const [errorType, setErrorType] = useState("");
  const initialState = {
    groupOrderName: null,
    perPersonBudget: null,
    expectedAttendees: null,
    selectedDate: null,
    selectedTime: null,
    selectedDateUser: null,
    selectedTimeUser: null,
    deliveryDate: null,
    deliveryTime: null,
    times: [],
    userTimes: [],
    dateError: false,
    timeError: false,
    userDateError: false,
    userTimeError: false,
    deliveryDateError: false,
    deliveryTimeError: false,
    deliveryInstructions: "",
    deliveryInstructionsLevel: "no",
  };

  function reducer(state, action) {
    switch (action.type) {
      case "reset":
        return initialState;
    }
    return {
      ...state,
      [action.field]: action.value,
    };
  }

  const [provider, dispatch] = useReducer(reducer, initialState);
  let {
    groupOrderName,
    perPersonBudget,
    expectedAttendees,
    deliveryInstructionsLevel,
  } = provider;

  const notificationMethods = [
    { id: "no", title: "No, only use these instructions for this order" },
    {
      id: "yes",
      title: "Yes, use for this and all future orders with this address",
    },
  ];

  useEffect(() => {
    if (visible) {
      dispatch({ field: "groupOrderName", value: order?.attributes?.name });
      dispatch({
        field: "perPersonBudget",
        value: parseFloat(order?.attributes?.subsidy).toFixed(2),
      });
      dispatch({
        field: "expectedAttendees",
        value: order?.attributes?.expected_bags,
      });

      dispatch({
        field: "deliveryInstructions",
        value: order.attributes?.address?.attributes?.delivery_instructions,
      });
    } else dispatch({ action: "reset" });
  }, [visible, order?.attributes]);

  const requestForDates = () => {};

  const updateGroupOrder = ({ payload, params }) => {
    store?.mealManagementStore?.updateGroupOrder(order?.id, payload, params);
  };

  const mutation = useMutation({
    mutationFn: updateGroupOrder,
    onSuccess: () => {
      getOrder();
      setModal(false);
    },
    onError: (error) => {
      console.error("Error updating record:", error);
      // Optionally show error message
    },
  });

  const submit = async (e) => {
    e.preventDefault();

    fetchOrderDeadline();
  };

  const fetchOrderDeadline = () => {
    if (provider.expectedAttendees && provider.perPersonBudget) {
      const params = {
        budget: provider.expectedAttendees * provider.perPersonBudget,
        target_time: order?.attributes?.meals[0]?.attributes?.target_time,
        meal_date: order?.attributes?.meals[0]?.attributes?.meal_date,
      };

      store?.mealManagementStore
        .fetchOrderDeadline(
          order?.attributes?.meals[0]?.attributes?.menu_id,
          params
        )
        .then((response) => {
          if (response?.data && !response?.data?.valid) {
            if (
              response?.data?.message?.includes("restaurant_need_more_time")
            ) {
              setErrorSupport(true);
              setErrorMessage(
                "Based on the budget for this order, the restaurant will need more time to prepare this order."
              );
              setErrorTitle(
                "The restaurant needs more time to prepare this order"
              );
              setErrorType(response?.data?.message);
            } else if (
              response?.data?.message?.includes("minimum_amount_not_met")
            ) {
              setErrorSupport(true);
              setErrorMessage(
                `Based on the budget for this order, the restaurants minimum of $${response?.data?.minimum_amount} will not be met. Please increase the budget or select another restaurant.`
              );
              setErrorTitle("Projected order value is below the order minimum");
              setErrorType(response?.data?.message);
            }
          } else if (response?.order_deadline) {
            setPayloads(response?.order_deadline);
          }
        });
    }
  };

  const setPayloads = (orderDeadline) => {
    let payload = {
      meal_series: {
        service: "grouped",
        name: sanitizeAndTrimInput(groupOrderName),
        subsidy: parseFloat(perPersonBudget).toFixed(2),
        expected_bags: expectedAttendees,
        // tip: provider?.tip === 'other' ? provider?.otherTip : provider?.tip,
        cutoff: dateTime.onlyTimeString(orderDeadline),
        meals_attributes: [
          {
            cutoff: orderDeadline,
            id: order?.attributes?.meals[0]?.attributes?.id,
          },
        ],
      },
    };

    if (provider.deliveryInstructionsLevel == "no") {
      payload.meal_series.delivery_instructions = sanitizeAndTrimInput(provider.deliveryInstructions);
    }

    let params = {
      location_id: order?.attributes?.location_id,
    };

    if (provider.deliveryInstructionsLevel == "yes")
      params.delivery_instructions = sanitizeAndTrimInput(provider.deliveryInstructions);

    mutation.mutate({ payload, params });
  };

  return (
    <div className="fixed inset-0 z-[50] bg-black bg-opacity-25 flex justify-center items-center">
      <div className="bg-white h-[90%] overflow-scroll rounded-2xl shadow-xl w-full sm:w-5/12 py-9 px-[37px] text-left">
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => setModal(false)}
            className="btn-style"
          >
            <FontAwesomeIcon
              style={{ width: 16, height: 26 }}
              icon={faXmark}
              size="2x"
              color="#BBC0C5"
            />
          </button>
        </div>

        <div className="flex w-full">
          <h3
            style={{ fontSize: screenType?.isTabletOrMobile ? 23 : 32 }}
            className="font-inter-semibold leading-6 text-black-light"
          >
            <span className="text-[32px]">Manage Your Group order</span>
          </h3>
        </div>

        <div className="flex flex-col mt-6 md:w-full sm:pb-0">
          <form onSubmit={submit} autoComplete="off">
            <div className="flex flex-col">
              <span className="text-md2 font-inter-semibold">
                Group order name*
              </span>
              <input
                required
                name="groupOrderNameField"
                className="input-gray-bg mt-3 focus:ring-0 address-inputs my-0 rounded-xl text-md w-full bg-background pl-4"
                placeholder="ex. Friday lunch & Learn"
                value={groupOrderName}
                onChange={(e) =>
                  dispatch({
                    field: "groupOrderName",
                    value: sanitizeAllowedChars(e.target.value),
                  })
                }
              />
            </div>

            <div className="flex flex-col mt-9">
              <span className="text-md2 font-inter-semibold">
                Budget and attendees*
              </span>
              <span className="text-sm mt-[6px]">
                The number of expected attendees affects the ensuring deadline
                to orders
              </span>

              <div className="mt-3 flex flex-col sm:grid sm:grid-cols-2 sm:gap-3">
                <div className="relative w-full">
                  <span
                    className={`absolute left-3 top-1/2 -translate-y-1/2 text-md ${
                      perPersonBudget ? "text-black" : "text-gray-400"
                    }`}
                  >
                    $
                  </span>
                  <input
                    required
                    type="number"
                    name="perPersonBudgetField"
                    className="pl-6 input-gray-bg focus:ring-0 address-inputs my-0 rounded-xl text-md w-full bg-background"
                    value={perPersonBudget}
                    onWheelCapture={(e) => e.target.blur()}
                    placeholder="0.00"
                    onChange={(e) =>
                      dispatch({
                        field: "perPersonBudget",
                        value: e.target.value,
                      })
                    }
                  />
                </div>

                <input
                  required
                  name="expectedAttendeesField"
                  className="input-gray-bg focus:ring-0 address-inputs my-0 rounded-xl text-md w-full bg-background pl-4"
                  placeholder="Expected attendees"
                  value={expectedAttendees}
                  max={300}
                  onWheelCapture={(e) => {
                    e.target.blur();
                  }}
                  type="number"
                  onBlur={requestForDates}
                  onChange={(e) =>
                    dispatch({
                      field: "expectedAttendees",
                      value: e.target.value,
                    })
                  }
                />
              </div>
            </div>

            <div className="flex flex-col mt-9">
              <span className="text-md2 font-inter-semibold">
                Edit delivery instructions
              </span>
              <textarea
                placeholder="ex. Park in the back entrance and call us when you arrive."
                className="input-gray-bg resize mt-3 focus:ring-0 address-inputs my-0 h-[130px] rounded-xl text-md w-full bg-background pl-4"
                type="text"
                value={provider.deliveryInstructions}
                onChange={(e) => {
                  dispatch({
                    field: "deliveryInstructions",
                    value: sanitizeAllowedChars(e.target.value),
                  });
                }}
              />

              <fieldset>
                <p className="text-md2 font-inter-semibold mt-[16px]">
                  Should we apply these instructions to all deliveries with this
                  address?
                </p>

                <div className="mt-[12px] space-y-2">
                  {notificationMethods.map((notificationMethod) => (
                    <div
                      key={notificationMethod.id}
                      className="flex items-center"
                    >
                      <input
                        id={notificationMethod.id}
                        name="notification-method"
                        type="radio"
                        defaultChecked={
                          deliveryInstructionsLevel === notificationMethod.id
                        }
                        onChange={(e) => {
                          dispatch({
                            field: "deliveryInstructionsLevel",
                            value: e.target.id,
                          });
                        }}
                        className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                      />
                      <label
                        htmlFor={notificationMethod.id}
                        className="ml-3 block text-sm font-medium leading-6 text-gray-900"
                      >
                        {notificationMethod.title}
                      </label>
                    </div>
                  ))}
                </div>
              </fieldset>
            </div>

            <div className="mt-6 w-full">
              <div className="hidden sm:flex justify-center items-center mt-6">
                <SubmitButtons
                  onClickLeft={() => setModal(false)}
                  leftButtonText={"Cancel"}
                  rightButtonText={"Apply"}
                />
              </div>
            </div>
          </form>

          {errorSupport && (
            <ErrorWithSupport
              visible={errorSupport}
              setModal={setErrorSupport}
              contactSupport={true}
              title={errorTitle}
              body={errorMessage}
              hideCancel={true}
              rightButtonText={"OK"}
              onClickRightButton={() => {
                setErrorSupport(false);
              }}
              handleLinkClick={() => {
                store.userStore.setFreshChatOpen(true);
              }}
              errorType={errorType}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default observer(ManageOrderDetailsModal);
